import type { Move } from '../../../schemas/move';

/**
 * Mosse iniziali del Monaco (manuale V1, rr. 4472-4542).
 *
 * - **5 mosse automatiche** (`type: 'starting'`): *Monasteri*, *Illuminazione*,
 *   *Sentieri del Monaco*, *Meditazione*, *Forza di Volontà*.
 * - **3 mosse di scelta** (`pickCount: 1`): *Regola della Battaglia*,
 *   *Regola del Lavoro*, *Regola della Pietà*. Manuale r. 4520:
 *   "*Inoltre, scegli una Regola*".
 *
 * **Pattern critico — *Sentieri del Monaco* (D2 della PR)**: mossa-config con
 * `choices` strutturate per le 7 restrizioni. Il "nome dell'ordine" (testo
 * libero) e la regola di violazione runtime (penalità sul costo di
 * *Meditazione*) restano in description (FI-18 strutturerà più avanti i
 * `choiceGroups` per scelte multi-gruppo + free-text).
 *
 * **Le 3 Regole sono mosse-trigger di prerequisiti** per molte mosse avanzate:
 *  - *Regola della Battaglia* → *Stile senz'Armi*, *Spirito Marziale*
 *  - *Regola del Lavoro* → *Mastro Speziale*, *Mastro Artigiano* (anyOf con
 *    `mago.laboratorio`)
 *  - *Regola della Pietà* → *Atto Compassionevole*
 */

const monasteri: Move = {
  id: 'monaco.monasteri',
  name: 'Monasteri',
  type: 'starting',
  classId: 'monaco',
  trigger:
    'Alla creazione del personaggio (configurazione dei Monasteri); quando ne visiti uno per la prima volta',
  description:
    'Il tuo ordine ha quattro *Monasteri* sparsi per il mondo. Segnane due sulla mappa e il GM segnerà gli altri due nel momento e nel luogo che gli sembrerà più appropriato e interessante.\n\nQuando **visiti un *Monastero* del tuo ordine per la prima volta**, il GM ti farà alcune domande a proposito: «*È questo il monastero che ti ha cresciuto?*», «*Per cosa è noto?*», «*Quali sono le dottrine qui insegnate e in che modo sono diverse dalle tue?*» Se rispondi, da quel momento in poi, tu e i tuoi alleati verrete sempre accolti e ospitati in quel *Monastero*. Là potrete *recuperare le forze* in metà del tempo, *salire di livello*, oppure *fare baldoria* con una celebrazione monastica. Inoltre, al suo interno avrete sempre un +1 alla reputazione per *reclutare*, e, anche se rifiutate dei candidati, non subite il -1 al prossimo tiro.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const illuminazione: Move = {
  id: 'monaco.illuminazione',
  name: 'Illuminazione',
  type: 'starting',
  classId: 'monaco',
  trigger:
    "Quando ti raccogli qualche minuto per cercare l'illuminazione in merito alla situazione corrente",
  description:
    "Tira+**SAG**. Con 10+, il GM ti darà un'informazione utile e dettagliata. Con 7-9, ottieni solo un sentore generale. Con 6-, la situazione è oscura e la prossima decisione che prendi ti causerà dei problemi.",
  roll: {
    stat: 'saggezza',
    on10plus: { text: "Il GM ti dà un'informazione utile e dettagliata." },
    on7to9: { text: 'Ottieni solo un sentore generale.' },
    on6minus: {
      text: 'La situazione è oscura e la prossima decisione che prendi ti causerà dei problemi.',
    },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const sentieriDelMonaco: Move = {
  id: 'monaco.sentieri-del-monaco',
  name: 'Sentieri del Monaco',
  type: 'starting',
  classId: 'monaco',
  trigger: 'Alla creazione del personaggio (configurazione del Sentiero / restrizione)',
  description:
    "Ti sei imposto una disciplina rigorosa per trovare la pace interiore nel tuo cammino verso l'illuminazione, e lo fai seguendo i dettami di una divinità o una filosofia di vita (scegli). Dà un nome al tuo ordine e decidi una restrizione imposta da esso:\n\nQuando **violi la restrizione**, ogni mossa che richiede prese di *Meditazione* costa una presa in più. Se violi nuovamente la restrizione mentre questa penalità è attiva, non potrai usare alcuna mossa che richieda prese di *Meditazione*. Queste penalità perdurano finché non avrai espirato il tuo peccato (a discrezione del GM).",
  tags: [],
  prerequisites: [],
  replaces: [],
  choices: [
    {
      id: 'volto-coperto',
      label: 'Volto coperto e distacco emotivo',
      description:
        'Devi tenere il viso coperto in ogni momento. Inoltre, devi mantenere un distacco emotivo da chiunque ed evitare il contatto fisico non accidentale o necessario (come, ad esempio, per usare una mossa o evitare che un alleato precipiti nel vuoto).',
    },
    {
      id: 'nessuna-resa',
      label: 'Mai rifiutare una sfida',
      description:
        'Non puoi mai rifiutare una sfida o ritirarti dalla battaglia, anche se i tuoi avversari sono in superiorità numerica o chiaramente molto più forti di te.',
    },
    {
      id: 'verita',
      label: 'Sempre la verità',
      description:
        "Non puoi mai mentire, bluffare, dire mezze verità con l'intento di ingannare, esagerare, dire bugie a fin di bene e così via. Ciò non ti impedisce di rimanere in silenzio se preferisci non dire qualcosa.",
    },
    {
      id: 'nonviolenza',
      label: 'Nonviolenza',
      description:
        'Non puoi mai uccidere un altro essere vivente. Non puoi attaccare un nemico a meno che tu non venga attaccato per primo, e anche in quel caso lo devi solo sopraffare, non uccidere. Non puoi mangiare carne salvo che non ci sia altra scelta.',
    },
    {
      id: 'nessuna-magia',
      label: 'Nessuna magia',
      description:
        'Non puoi mai *lanciare incantesimi*, utilizzare magie (a parte tutte le mosse del Monaco e i relativi effetti) o ricorrere a oggetti magici, neanche alle Pozioni Curative.',
    },
    {
      id: 'poverta',
      label: 'Povertà',
      description:
        'Devi rifiutare quanti più beni terreni possibili. Non puoi mai possedere più cose di quante tu sia in grado di trasportare sulla tua persona e che siano essenziali per la tua sopravvivenza, e non puoi accumulare ricchezze, beni o oggetti di lusso.',
    },
    {
      id: 'umilta',
      label: 'Umiltà',
      description:
        'Non puoi mai cercare la gloria personale o accettare riconoscimenti pubblici per le tue azioni, né puoi esaltare le tue capacità o vantarti delle tue conquiste. Quando qualcuno ti elogia direttamente, devi deviare il complimento o attribuire il merito a una forza superiore o ai tuoi compagni.',
    },
  ],
  choiceCount: 1,
  narrativeFields: [
    {
      id: 'nome-ordine',
      label: "Nome dell'ordine",
      placeholder: "es. Ordine dell'Acqua Quieta, Confraternita dei Silenti, Via del Vento…",
      required: true,
      multiline: false,
    },
  ],
};

const meditazione: Move = {
  id: 'monaco.meditazione',
  name: 'Meditazione',
  type: 'starting',
  classId: 'monaco',
  trigger: 'Quando ti *accampi*',
  description:
    'Al risveglio perdi tutte le prese che avevi in precedenza e prendi 3. Il numero massimo di prese che puoi avere è 3.',
  tags: [],
  prerequisites: [],
  replaces: [],
};

const forzaDiVolonta: Move = {
  id: 'monaco.forza-di-volonta',
  name: 'Forza di Volontà',
  type: 'starting',
  classId: 'monaco',
  trigger: 'Quando imponi la tua ferrea volontà per manipolare la realtà',
  description:
    "Se non indossi armatura, non imbracci uno scudo, e hai prese di *Meditazione* a sufficienza, scegli uno di questi effetti:\n\n- lenire il dolore e guarire a te stesso o a un alleato che stai toccando 1d8 PF per ogni presa che sei disposto a rischiare (al massimo 3 prese);\n- ottenere +1 Armatura finché non usi di nuovo questa mossa (1 presa);\n- fare al GM 3 domande dalla lista di *discernere realtà* (1 presa);\n- far sì che il GM ti dica qualcosa di interessante e che può tornarti utile su un argomento a tua scelta (1 presa). Il GM potrebbe chiederti che tipo di illuminazione hai ricevuto;\n- evitare, finché non rischi o spendi prese di *Meditazione*, la penalità dovuta a una Debilitazione (indica quale tra quelle che hai segnato) o di dover *sfidare il pericolo* causato da un'afflizione come stordimento, fame, sanguinamento, ecc. (1 presa).\n\nQuindi tira+**SAG**. Con 7-9, ottieni l'effetto desiderato e spendi il numero di prese previste. Con 10+, invece, ottieni l'effetto senza spendere alcuna presa. Con 6-, spendi le prese, ma non ottieni alcunché.\n\nSe prendi questa mossa con un avanzamento multiclasse, ottieni anche *Sentieri del Monaco* e *Meditazione*.",
  roll: {
    stat: 'saggezza',
    on10plus: { text: "Ottieni l'effetto senza spendere alcuna presa." },
    on7to9: { text: "Ottieni l'effetto desiderato e spendi il numero di prese previste." },
    on6minus: { text: 'Spendi le prese, ma non ottieni alcunché.' },
  },
  tags: [],
  prerequisites: [],
  replaces: [],
};

const regolaDellaBattaglia: Move = {
  id: 'monaco.regola-della-battaglia',
  name: 'Regola della Battaglia',
  type: 'starting',
  classId: 'monaco',
  trigger: 'Quando *tagli e spacchi* a mani nude o con un bastone',
  description:
    "Puoi tirare+**SAG** invece che +**FOR**. Inoltre, puoi effettuare attacchi a mani nude o usare *Proiezione della Volontà* contro creature con forme pericolose, tipo un elementale del fuoco, senza dover *sfidare il pericolo* dovuto alle loro difese naturali. Infine, ignori l'etichetta *smussata* dei tuoi attacchi a mani nude.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const regolaDelLavoro: Move = {
  id: 'monaco.regola-del-lavoro',
  name: 'Regola del Lavoro',
  type: 'starting',
  classId: 'monaco',
  trigger: 'Quando ti *accampi*',
  description:
    "Puoi fare una delle seguenti cose:\n\n- costruire un oggetto di facile realizzazione;\n- apportare semplici riparazioni a un oggetto danneggiato;\n- preparare 1 utilizzo di Attrezzatura da Avventuriero o di Erbe e Unguenti;\n- condurre una ricerca approfondita su di un argomento specifico, ottenendo un'informazione utile e dettagliata in merito a esso da parte del GM.\n\nDescrivi cosa vuoi ottenere esattamente, e concorda con il GM che attrezzi e materiali userai, o a quali fonti di conoscenza intendi attingere. Se trovate un accordo, ottieni quel che desideri, ma se costruisci o ripari un oggetto, oppure conduci una ricerca complessa, il GM potrebbe chiederti di usare più volte questa mossa prima di arrivare al risultato voluto.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

const regolaDellaPieta: Move = {
  id: 'monaco.regola-della-pieta',
  name: 'Regola della Pietà',
  type: 'starting',
  classId: 'monaco',
  trigger:
    'Quando usi Bende o Erbe e Unguenti; quando ti *accampi* e racconti una parabola o spieghi ai tuoi compagni un concetto filosofico per dare loro sollievo e lenire le fatiche del viaggio',
  description:
    "Quando **usi Bende o Erbe e Unguenti**, ignori l'etichetta *lento*. Inoltre, quando **ti *accampi* e racconti una parabola o spieghi ai tuoi compagni un concetto filosofico per dare loro sollievo e lenire le fatiche del viaggio**, al risveglio tu e i tuoi alleati recuperate +1d6 PF.",
  tags: [],
  prerequisites: [],
  replaces: [],
};

/**
 * Mosse iniziali AUTOMATICHE (5): prese tutte al livello 1 senza scelta.
 */
export const monacoStartingAutoMoves: readonly Move[] = [
  monasteri,
  illuminazione,
  sentieriDelMonaco,
  meditazione,
  forzaDiVolonta,
] as const;

/**
 * Mosse iniziali di SCELTA (3): il giocatore ne sceglie UNA al livello 1
 * (`pickCount: 1`). Le 3 Regole sono trigger di prerequisiti per molte
 * mosse avanzate.
 */
export const monacoStartingChoiceMoves: readonly Move[] = [
  regolaDellaBattaglia,
  regolaDelLavoro,
  regolaDellaPieta,
] as const;

/** Tutte le mosse iniziali (auto + scelta). */
export const monacoInitialMoves: readonly Move[] = [
  ...monacoStartingAutoMoves,
  ...monacoStartingChoiceMoves,
];
